import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useState } from "react";

interface HydrationGoalTrackerProps {
  currentIntake: number; // in ml
  dailyGoal?: number; // in ml, default 2000ml (2L)
  onGoalUpdate?: (newGoal: number) => void;
}

export const HydrationGoalTracker = ({ 
  currentIntake, 
  dailyGoal = 2000,
  onGoalUpdate
}: HydrationGoalTrackerProps) => {
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [newGoal, setNewGoal] = useState(dailyGoal.toString());
  
  const percentage = Math.min((currentIntake / dailyGoal) * 100, 100);
  const remaining = Math.max(dailyGoal - currentIntake, 0);
  const isGoalMet = currentIntake >= dailyGoal;

  const handleSaveGoal = () => {
    const goalValue = parseInt(newGoal);
    if (goalValue > 0 && goalValue <= 10000) {
      onGoalUpdate?.(goalValue);
      setIsEditingGoal(false);
    }
  };

  return (
    <Card className="p-8 bg-card border border-border/60 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-border/60 ${isGoalMet ? 'ring-primary/40' : ''}`}>
            <img src="/daily-hydration-goal-icon.png" alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">A measure of care</p>
            <h3 className="font-serif text-2xl leading-tight">Daily <em className="italic font-normal">hydration</em></h3>
          </div>
        </div>
        {onGoalUpdate && (
          <Dialog open={isEditingGoal} onOpenChange={setIsEditingGoal}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground"
                onClick={() => setNewGoal(dailyGoal.toString())}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Set daily hydration goal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Goal (ml)</label>
                  <Input
                    type="number"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    placeholder="2000"
                    min="500"
                    max="10000"
                    step="100"
                  />
                  <p className="text-xs italic text-muted-foreground">
                    Recommended: 2000–3000 ml per day (8–12 glasses).
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditingGoal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveGoal}>Save goal</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-baseline">
          <span className="font-serif text-4xl text-foreground">
            {currentIntake}
            <span className="text-lg text-muted-foreground ml-1">ml</span>
          </span>
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
            of {dailyGoal} ml
          </span>
        </div>
        <Progress value={percentage} className="h-[3px]" />
        <p className="text-xs italic text-muted-foreground text-center pt-1">
          {isGoalMet ? (
            <span className="text-primary">Goal reached — beautifully done.</span>
          ) : (
            <>{remaining} ml remaining to reach your goal.</>
          )}
        </p>
      </div>
    </Card>
  );
};
